import { OptionType, Strategy } from '@oai-frontend-monorepo/types';
import parseStrikeFromSymbol from '../../symbol/parseStrikeFromSymbol';
import parseOptionTypeFromSymbol from '../../symbol/parseOptionTypeFromSymbol';

export const calculateIntrinsicPrice = (
  strategy?: Strategy,
  symbols?: string[],
  sharePrice?: number
) => {
  if (!strategy || !symbols || !sharePrice) {
    return undefined;
  }

  switch (strategy) {
    case Strategy.LONG_CALL: {
      const strike = parseStrikeFromSymbol(symbols[0]);

      return Math.max(0, sharePrice - strike);
    }
    case Strategy.LONG_PUT: {
      const strike = parseStrikeFromSymbol(symbols[0]);

      return Math.max(0, strike - sharePrice);
    }

    case Strategy.SHORT_PUT_SPREAD: {
      const put1 = parseStrikeFromSymbol(symbols[0]);
      const put2 = parseStrikeFromSymbol(symbols[1]);

      const longPut = Math.min(put1, put2);
      const shortPut = Math.max(put1, put2);

      const spreadWidth = shortPut - longPut;

      if (sharePrice > shortPut) {
        return 0;
      }

      if (sharePrice < longPut) {
        return spreadWidth;
      }

      return shortPut - sharePrice;
    }

    case Strategy.LONG_CALL_SPREAD: {
      const call1 = parseStrikeFromSymbol(symbols[0]);
      const call2 = parseStrikeFromSymbol(symbols[1]);

      const longCall = Math.min(call1, call2);
      const shortCall = Math.max(call1, call2);

      const spreadWidth = shortCall - longCall;

      const aboveLongCall = sharePrice - longCall;

      if (aboveLongCall > spreadWidth) {
        return spreadWidth;
      }

      if (aboveLongCall < 0) {
        return 0;
      }

      return aboveLongCall;
    }

    case Strategy.SHORT_CALL_SPREAD: {
      const callStrike1 = parseStrikeFromSymbol(symbols[0]); // strike
      const callStrike2 = parseStrikeFromSymbol(symbols[1]); // strike

      const longCallStrike = Math.max(callStrike1, callStrike2); // greater strike
      const shortCallStrike = Math.min(callStrike1, callStrike2); // lesser strike

      const spreadWidth = longCallStrike - shortCallStrike;

      if (sharePrice < shortCallStrike) {
        return 0;
      }

      if (sharePrice > longCallStrike) {
        return spreadWidth;
      }

      return sharePrice - shortCallStrike; // difference between share price and short call
    }

    case Strategy.LONG_PUT_SPREAD: {
      const put1 = parseStrikeFromSymbol(symbols[0]);
      const put2 = parseStrikeFromSymbol(symbols[1]);

      const longPut = Math.max(put1, put2);
      const shortPut = Math.min(put1, put2);

      const spreadWidth = longPut - shortPut;

      const belowLongPut = longPut - sharePrice;

      if (belowLongPut > spreadWidth) {
        return spreadWidth;
      }

      if (belowLongPut < 0) {
        return 0;
      }

      return belowLongPut;
    }

    case Strategy.IRON_BUTTERFLY:
    case Strategy.IRON_CONDOR: {
      const calls = symbols
        .filter((s) => parseOptionTypeFromSymbol(s) === OptionType.CALL)
        .map((s) => parseStrikeFromSymbol(s));

      const puts = symbols
        .filter((s) => parseOptionTypeFromSymbol(s) === OptionType.PUT)
        .map((s) => parseStrikeFromSymbol(s));

      const longCall = Math.max(...calls);

      const shortCall = Math.min(...calls);

      const shortPut = Math.max(...puts);
      const longPut = Math.min(...puts);

      const spreadWidth = Math.abs(shortCall - longCall);

      // is it between the shortCall and shortPut or exactly at one of them?
      if (
        sharePrice === shortCall ||
        sharePrice === shortPut ||
        (sharePrice >= shortPut && sharePrice <= shortCall)
      ) {
        return 0;
      }

      if (sharePrice > longCall || sharePrice < longPut) {
        return spreadWidth;
      }

      if (sharePrice < shortPut) {
        return shortPut - sharePrice;
      }

      return sharePrice - shortCall;
    }

    default: {
      return undefined;
    }
  }
};
